import { type Tenants, logError } from '@gp/base'
import { type AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'

import { type CustomerStore, customerStore } from '@/store/CustomerStore'
import { EsumErrorStatus, FetchState } from '@/types/enum'
import { type IGetOfferResponse, type IOfferData } from '@/types/offer'
import { isNextCalendarDay } from '@/utils/date'
import { delay } from '@/utils/delay'
import { getTenant } from '@/utils/getTenant'

import { activateOffer } from './api/activateOffer'
import { getAgreements } from './api/getAgreements'
import { getOffer } from './api/getOffer'
import { setAgreements } from './api/setAgreements'

const HTTP_ERROR_STATUS_MAP: Partial<Record<number, EsumErrorStatus>> = {
  403: EsumErrorStatus.forbidden,
  400: EsumErrorStatus.noCard,
  401: EsumErrorStatus.noAuth,
}

const ACTIVATE_ANIMATION_DELAY_MS = 1000

type CustomerStoreModel = Pick<
  CustomerStore,
  'isAuthorized' | 'accessTokenState'
>

export class EsumStore {
  readonly tenant: Tenants
  private readonly customerStore: CustomerStoreModel

  offerData: IGetOfferResponse | null = null
  fetchState: FetchState = FetchState.initial
  activateState: FetchState = FetchState.initial
  errorStatus: EsumErrorStatus | null = null

  constructor(tenant: Tenants, customerStore: CustomerStoreModel) {
    this.tenant = tenant
    this.customerStore = customerStore

    makeAutoObservable(this, { tenant: false })
  }

  get canActivateToday(): boolean {
    const progress = this.offerData?.progress
    const lastActivated = progress?.last_activated_dt

    if (!lastActivated) return true

    return isNextCalendarDay(lastActivated, progress?.timezone ?? undefined)
  }

  get isActivated(): boolean {
    return this.offerData?.progress != null && !this.canActivateToday
  }

  get offer(): IOfferData | null {
    return this.offerData?.offer_data ?? null
  }

  get isExpired(): boolean {
    return (
      !!this.offer?.end_of_period_delivery &&
      new Date(this.offer.end_of_period_delivery) < new Date()
    )
  }

  get isLoading(): boolean {
    return (
      this.customerStore.accessTokenState === FetchState.pending ||
      this.fetchState === FetchState.pending
    )
  }

  get isActivating(): boolean {
    return this.activateState === FetchState.pending
  }

  get isAuthError(): boolean {
    return (
      !this.customerStore.isAuthorized ||
      this.customerStore.accessTokenState === FetchState.rejected
    )
  }

  get isLoadError(): boolean {
    return this.fetchState === FetchState.rejected && this.errorStatus === null
  }

  // Если карта есть, то бекенд вернет оффер
  async hasCard(): Promise<boolean> {
    try {
      await getOffer(this.tenant)
      return true
    } catch (error) {
      const status = (error as AxiosError)?.response?.status
      if (status === 400) return false

      throw error
    }
  }

  async fetchOffer() {
    if (this.fetchState === FetchState.pending) return

    try {
      runInAction(() => {
        this.fetchState = FetchState.pending
      })

      const data: IGetOfferResponse = await getOffer(this.tenant)

      runInAction(() => {
        this.fetchState = FetchState.fulfilled
        this.offerData = data
        this.errorStatus = null
      })
    } catch (error) {
      const status = (error as AxiosError)?.response?.status

      runInAction(() => {
        this.fetchState = FetchState.rejected
        this.errorStatus =
          (status != null ? HTTP_ERROR_STATUS_MAP[status] : null) ?? null
      })
      logError({
        error: error as Error,
        tag: 'esum-store-fetch-offer',
        extraItems: { tenant: this.tenant },
      })
    }
  }

  private async ensureAgreements() {
    try {
      const agreements = await getAgreements(this.tenant)
      const allNull = Object.values(agreements).every((v) => v == null)

      if (allNull) {
        await setAgreements(this.tenant)
      }
    } catch (error) {
      logError({
        error: error as Error,
        tag: 'esum-store-agreements',
        extraItems: {
          tenant: this.tenant,
        },
      })

      throw error
    }
  }

  async activate() {
    if (this.activateState === FetchState.pending) return

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    try {
      runInAction(() => {
        this.activateState = FetchState.pending
      })

      await this.ensureAgreements()

      const [data] = await Promise.all([
        activateOffer({
          tenant: this.tenant,
          timeZone,
        }),
        delay(ACTIVATE_ANIMATION_DELAY_MS),
      ])

      if (!this.offerData) {
        logError({
          error: new Error('offerData is null on activate'),
          tag: 'esum-store-activate-offer',
          extraItems: { tenant: this.tenant, timeZone },
        })
        runInAction(() => {
          this.activateState = FetchState.fulfilled
        })
        return
      }

      runInAction(() => {
        this.activateState = FetchState.fulfilled
        this.offerData = {
          ...this.offerData!,
          offer_data: data.offer_data,
          progress: data.progress,
        }
      })
    } catch (error) {
      runInAction(() => {
        this.activateState = FetchState.rejected
      })
      logError({
        error: error as Error,
        tag: 'esum-store-activate-offer',
        extraItems: {
          tenant: this.tenant,
          timeZone,
        },
      })
    }
  }
}

export const esumStore = new EsumStore(getTenant(), customerStore)
